/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */

/*
 * ProductImageJob.jsx — จัดการภาพสินค้า   (งานข้อ 3 · Step 4.5)
 *
 * โครงเดียวกับ ProductAiImage.jsx — ถือ state ตัวเอง ต่อเข้า Product.jsx 3 บรรทัด
 * ไม่แตะฟอร์มสินค้าเดิม (กัน mass assignment ใน product.con.php:377)
 *
 * 3 ชนิดงาน (ราคาจากการทดสอบจริง 7 ก.ย.)
 *   ลบพื้นหลัง          1 เครดิต   รูปถ่ายมาดี พื้นเรียบ
 *   AI เก็บงาน + ลบพื้นหลัง  9 เครดิต   รูปถือในมือ/พื้นรก — AI เอามือออกก่อน
 *   ใส่รหัสสินค้า        ฟรี        ทำในเครื่องด้วย PHP GD เสร็จทันที
 *
 * งานฟรีกดแล้วเสร็จเลย (run_now) · งานที่เสียเงินเข้าคิวให้ worker ทำ (cron ทุก 1 นาที)
 * หน้าจอ poll ทุก 6 วินาที — ห้ามถี่กว่า 5 วิ เพราะ preventDuplicate ของระบบเดิม
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';
import ImageReview from 'layouts/_entrio/ImageReview';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import RetryImageJobDialog from 'layouts/_entrio/RetryImageJobDialog';
import ImagePickerDialog from 'layouts/_entrio/ImagePickerDialog';
import nopic from 'assets/images/nopic.jpg';
import GalleryLink from 'layouts/_entrio/GalleryLink';
import jobCreditText, { jobErrorText, jobDateText } from 'layouts/_entrio/jobCreditText';
import {
  ReviewThumb,
  CardMenu,
  ActionRow,
  DownloadButton,
  SelectBar,
} from 'layouts/_entrio/ResultCardParts';
import useBulkDownload from 'layouts/_entrio/useBulkDownload';

const statusList = {
  QUEUED: { name: 'รอคิว', color: 'secondary' },
  PROCESSING: { name: 'กำลังทำ', color: 'warning' },
  DONE: { name: 'สำเร็จ', color: 'success' },
  FAILED: { name: 'ไม่สำเร็จ', color: 'error' },
  CANCELLED: { name: 'ยกเลิก', color: 'secondary' },
};

const typeName = {
  BG_REMOVE: 'ลบพื้นหลัง',
  AI_CLEAN: 'AI เก็บงาน + ลบพื้นหลัง',
  SKU_STAMP: 'ใส่รหัสสินค้า',
};

/* กรอบส่วน — โทนเดียวกับแท็บข้อมูล AI / ภาพ AI */
const sectionSx = {
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: 2,
  p: 3,
  bgcolor: '#ffffff',
};

const eyebrow = (text) => (
  <MDTypography
    variant='caption'
    fontWeight='bold'
    color='text'
    sx={{ letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.7 }}
  >
    {text}
  </MDTypography>
);

function ProductImageJob({ sku, onOpenGallery }) {
  const mod = 'image_job';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const { id } = useParams();

  const [jobType, setJobType] = useState('BG_REMOVE');
  const [stampSku, setStampSku] = useState(false);
  const [sourceId, setSourceId] = useState('main');

  // can_run = null จนกว่าโหลดสิทธิ์เสร็จ — กันป้าย "ดูได้อย่างเดียว" แวบขึ้นทุกครั้งที่เปิด (18 ต.ค. 2569)
  const [meta, setMeta] = useState({ job_types: [], can_run: null, usage: null });
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0); // ทั้งหมดที่มี · แสดงทีละ 50 กด "แสดงเพิ่ม" (ตรวจการใช้งาน 1.8 · 1 พ.ย. 2569)
  const limitRef = useRef(50);
  /* งานของสินค้านี้เห็นเฉพาะที่ตัวเองสั่ง · ผู้ดูแล AI เปิด "ดูของทุกคน" ได้ (27 ต.ค. 2569) */
  const [canViewAll, setCanViewAll] = useState(false);
  const [scopeAll, setScopeAll] = useState(false);
  const scopeRef = useRef(false);
  const [reviewId, setReviewId] = useState(null);
  const [galleryMessage, setGalleryMessage] = useState('');
  const [retryRow, setRetryRow] = useState(null);
  const retryBusy = useRef(false);
  const [images, setImages] = useState([]);
  const [mainImage, setMainImage] = useState({ id: 0, url: '' }); // ภาพหลัก — ไว้โชว์ตัวอย่างในช่องภาพต้นทาง

  /* ── ช่องภาพต้นทาง: รูปย่อ + ชื่อ (ผู้ใช้ทัก 23 ก.ย. ว่าเห็นแต่ "ภาพ #23802" ไม่รู้ว่ารูปไหน) ── */
  const sourceThumb = () => {
    if (sourceId === 'main') return mainImage.url;
    const im = images.find((x) => String(x.id) === String(sourceId));
    return im ? im.image_url : '';
  };
  const sourceLabel = (v) => (v === 'main' ? 'รูปหลักของสินค้า' : `รูปที่เลือก (#${v})`);
  const [pickOpen, setPickOpen] = useState(false);
  const [delRow, setDelRow] = useState(null);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const notifyTimer = useRef(null);
  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // มุมล่างขวาบังปุ่มปิดหน้าต่างตรวจภาพ
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(null)}
      />
    );

    /* ข้อความผิดพลาดค้าง 8 วิ ให้อ่านทัน · สำเร็จ 3 วิ · ข้อความใหม่ล้างตัวจับเวลาเก่า ไม่ให้ปิดก่อนเวลา (18 ต.ค. 2569) */
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(
      function () {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  /* ดาวน์โหลดภาพผลลัพธ์ ทีละภาพ / ติ๊กหลายภาพเป็น zip (A2 · 25 ต.ค. 2569) · ชื่อไฟล์ = รหัสสินค้า_ชนิดงาน_เลขงาน */
  const dl = useBulkDownload();
  const dlName = sku || `สินค้า${id}`;
  const dlItem = (row) => ({
    id: row.id,
    url: row.output_url,
    name: `${dlName}_${typeName[row.job_type] || row.job_type}_${row.id}`,
  });
  const dlAll = list.filter((row) => row.output_url).map(dlItem);

  const loadMeta = () => {
    FetchData('GET', `/${mod}`, app.token, {})
      .then((result) => {
        if (result.status === 'ok' && alive.current) setMeta(result);
      })
      .catch(() => {});
  };

  const loadList = () => {
    FetchData('OPTIONS', `/${mod}/by_product`, app.token, {
      product_id: id,
      scope: scopeRef.current ? 'all' : 'mine',
      limit: limitRef.current,
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current) return;
        /* สลับ "ดูของทุกคน" ระหว่างคำขอค้าง — คำตอบของมุมมองเดิมมาทีหลังห้ามทับรายการ (รีเช็ค 27 ต.ค. 2569) */
        if (result.status === 'ok' && (result.scope === 'all') !== scopeRef.current) return;
        setLoading(false);

        if (result.status === 'ok') {
          setList(result.data || []);
          setTotal(Number(result.total) || 0);
          setCanViewAll(result.can_view_all === 'Y');
          setImages(result.images || []);
          setMainImage({ id: result.main_image_id || 0, url: result.main_image_url || '' });
          if (result.usage) setMeta((m) => ({ ...m, usage: result.usage }));
        } else {
          showNotify(t(`alert.loaderror`), 'error');
        }
      })
      /* เน็ตหลุด/เซิร์ฟเวอร์ไม่ตอบ — ปลดม่านโหลด ไม่ให้หน้าค้าง (16 ต.ค. 2569) */
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(t(`alert.loaderror`), 'error');
      });
  };

  useEffect(() => {
    if (id) {
      loadMeta();
      loadList();
    }
  }, [id]);

  /* งานที่ยังไม่จบ — poll ทุก 6 วินาที */
  const hasPending = list.some((j) => j.status === 'QUEUED' || j.status === 'PROCESSING');

  useEffect(() => {
    if (!hasPending) return undefined;
    const timer = setInterval(() => loadList(), 6000);
    return () => clearInterval(timer);
  }, [hasPending]);

  const selectedType = meta.job_types.find((x) => x.id === jobType);

  const submit = () => {
    setLoading(true);

    const payload = { product_id: id, job_type: jobType, stamp_sku: stampSku ? 'Y' : 'N' };
    if (sourceId !== 'main') payload.image_ids = [Number(sourceId)];

    FetchData('PUT', `/${mod}`, app.token, payload)
      .then((result) => {
        if (!alive.current) return;

        if (result.status !== 'ok') {
          setLoading(false);
          /* รหัส error เป็นไทยจากแผนที่กลาง (โควตา · ปิดระบบ · สิทธิ์ · ไม่มีภาพ ฯลฯ) (16 ต.ค. 2569) */
          showNotify(jobErrorText(result.msg) || t(`alert.saveerror`), 'error');
          return;
        }

        /* งานฟรีทำเสร็จทันที ไม่ต้องรอ cron */
        if (jobType === 'SKU_STAMP') {
          FetchData('OPTIONS', `/${mod}/run_now`, app.token, {
            id: result.job_ids[0],
            _t: Date.now(),
          })
            .then((run) => {
              if (!alive.current) return;
              setLoading(false);
              if (run.status === 'ok' && run.output_path)
                showNotify('ใส่รหัสสินค้าเรียบร้อย ตรวจภาพก่อนเก็บเข้าแกลเลอรี', 'success');
              else showNotify('ใส่รหัสยังไม่สำเร็จ ตรวจสถานะและรายละเอียดของงานด้านล่าง', 'error');
              loadList();
            })
            .catch(() => {
              if (alive.current) {
                setLoading(false);
                showNotify('ยังไม่เห็นผลใส่รหัส โหลดหน้าใหม่ก่อนสั่งซ้ำ', 'error');
              }
            });
          return;
        }

        setLoading(false);
        showNotify('ส่งงานเข้าคิวแล้ว · เสร็จภายใน 1–2 นาที ดูที่รายการด้านล่าง', 'success');
        loadList();
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(t(`alert.saveerror`), 'error');
      });
  };

  const approve = (row) => {
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/approve`, app.token, { id: row.id, _t: Date.now() })
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);
        if (result.status === 'ok') {
          showNotify('เก็บเข้าแกลเลอรีแล้ว', 'success');
          setGalleryMessage(
            result.primary_image_set === 'Y'
              ? 'เก็บเข้าแกลเลอรีแล้ว · ตั้งเป็นรูปหลักเพราะสินค้านี้ยังไม่มีรูปหลัก'
              : result.primary_image_set === 'N'
              ? 'เก็บเข้าแกลเลอรีแล้ว · รูปหลักเดิมยังอยู่'
              : 'เก็บเข้าแกลเลอรีแล้ว · ตรวจรูปหลักได้ที่ เปิดแกลเลอรีสินค้า'
          );
          loadList();
        } else if (result.msg === 'already_approved') {
          showNotify('ภาพนี้อยู่ในแกลเลอรีแล้ว', 'warning');
        } else {
          showNotify(jobErrorText(result.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(t(`alert.saveerror`), 'error');
      });
  };

  const retry = (row) => {
    if (!row || retryBusy.current || meta.can_run !== 'Y' || meta.usage?.kill_switch === 'Y')
      return;
    retryBusy.current = true;
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/retry`, app.token, { id: row.id, _t: Date.now() })
      .then((result) => {
        retryBusy.current = false;
        if (!alive.current) return;
        setLoading(false);
        if (result.status === 'ok') {
          setRetryRow(null);
          showNotify('สั่งทำใหม่แล้ว', 'success');
          loadList();
        } else {
          showNotify(jobErrorText(result.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        retryBusy.current = false;
        if (alive.current) {
          setLoading(false);
          showNotify('ส่งคำขอไม่สำเร็จ โหลดหน้าใหม่แล้วลองอีกครั้ง', 'error');
        }
      });
  };

  /* ลบ = ยกเลิกงานที่รอคิวด้วย (worker ทำเฉพาะงานที่ยังอยู่ในรายการ) · งานที่กำลังทำ backend ไม่ยอม (18 ต.ค. 2569) */
  const remove = () => {
    if (!delRow) return;
    const cancelJob = delRow.status === 'QUEUED';
    const failText = cancelJob ? 'ยกเลิกไม่สำเร็จ' : t(`alert.deleteerror`);
    setLoading(true);
    FetchData('DELETE', `/${mod}/${delRow.id}`, app.token, {})
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);
        setDelRow(null);
        if (result.status === 'ok') {
          showNotify(cancelJob ? 'ยกเลิกงานแล้ว' : t(`alert.deletecomplete`), 'success');
        } else {
          showNotify(jobErrorText(result.msg) || failText, 'error');
        }
        loadList();
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(failText, 'error');
      });
  };

  const { usage } = meta;
  const canRun = meta.can_run === 'Y' && (!usage || usage.kill_switch !== 'Y');
  let startCost = '';
  if (selectedType) {
    startCost = Number(selectedType.credits) > 0 ? ` · ${selectedType.credits} เครดิต` : ' · ฟรี';
  }
  const cancelling = Boolean(delRow && delRow.status === 'QUEUED');

  return (
    <MDBox pt={1}>
      {/* ── ส่วนหัว ─────────────────────────────────────────────────────── */}
      <MDBox
        display='flex'
        alignItems='flex-start'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={1}
      >
        <MDBox>
          <MDTypography variant='h5' fontWeight='medium'>
            จัดการภาพสินค้า
          </MDTypography>
          {onOpenGallery && <GalleryLink productId={id} token={app.token} onOpen={onOpenGallery} />}
          {galleryMessage && (
            <MDTypography variant='caption' display='block' aria-live='polite'>
              {galleryMessage}
            </MDTypography>
          )}
        </MDBox>
        <MDBox display='flex' gap={1} alignItems='center' flexWrap='wrap'>
          {usage && (
            <Chip
              size='small'
              variant='outlined'
              label={`เดือนนี้ทั้งร้าน ${usage.month_images}${
                usage.quota > 0 ? `/${usage.quota}` : ''
              } ภาพ${usage.my_images != null ? ` · คุณ ${usage.my_images}` : ''}`}
            />
          )}
          {usage && usage.kill_switch === 'Y' && (
            <Chip size='small' color='error' label='AI ปิดทั้งร้าน' />
          )}
        </MDBox>
      </MDBox>

      <Divider sx={{ my: 2 }} />

      {/* ── แผงสั่งงาน — บัญชีดูอย่างเดียวพับเหลือป้าย แบบหน้าภาพ AI (18 ต.ค. 2569) ──── */}
      {meta.can_run === 'N' && (
        <MDBox
          mb={2}
          px={1.5}
          py={1.25}
          display='flex'
          gap={1}
          alignItems='flex-start'
          role='status'
          sx={{ borderRadius: 2, bgcolor: 'rgba(251,140,0,0.12)', color: '#9A4D00' }}
        >
          <Icon fontSize='small' sx={{ mt: '2px' }}>
            visibility
          </Icon>
          <MDTypography variant='button' fontWeight='medium' color='inherit' lineHeight={1.45}>
            บัญชีนี้ยังสร้างภาพ AI ไม่ได้ · ขอสิทธิ์จากผู้ดูแลระบบ
          </MDTypography>
        </MDBox>
      )}
      <MDBox sx={{ ...sectionSx, display: meta.can_run === 'N' ? 'none' : undefined }}>
        {eyebrow('สั่งงาน')}

        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={4}>
            <MDInput
              label='ชนิดงาน'
              fullWidth
              select
              value={jobType}
              onChange={(e) => setJobType(e.target.value)}
              InputLabelProps={{ shrink: true }}
              title={selectedType ? selectedType.hint : undefined}
            >
              {meta.job_types.map((x) => (
                <MenuItem key={x.id} value={x.id}>
                  {x.name} · {x.credits > 0 ? `${x.credits} เครดิต` : 'ฟรี'}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {/* ภาพต้นทาง — กดแล้วเปิดแกลเลอรีแบบตาราง (18 ต.ค. 2569 · เดิมเป็น dropdown 20 รูป และภาพหลักขึ้นซ้ำ 2 ครั้ง) */}
          <Grid item xs={12} md={3} display='flex' alignItems='center'>
            <ButtonBase
              onClick={() => setPickOpen(true)}
              aria-label='เลือกรูปต้นทางจากแกลเลอรี'
              title='เลือกจากแกลเลอรีสินค้า'
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 1.25,
                p: 0.75,
                textAlign: 'left',
                border: '1px solid',
                borderColor: 'grey.400',
                borderRadius: 2,
                '&:hover, &.Mui-focusVisible': { borderColor: 'info.main' },
              }}
            >
              <MDBox
                component='img'
                src={sourceThumb() || nopic}
                alt=''
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = nopic;
                }}
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  flexShrink: 0,
                }}
              />
              <MDBox minWidth={0} flex={1}>
                <MDTypography variant='caption' color='text' display='block' lineHeight={1.3}>
                  รูปต้นทาง
                </MDTypography>
                <MDTypography variant='button' fontWeight='medium' display='block' noWrap>
                  {sourceLabel(sourceId)}
                </MDTypography>
              </MDBox>
              <Icon sx={{ color: 'info.main', flexShrink: 0 }}>photo_library</Icon>
            </ButtonBase>
          </Grid>

          {/* สวิตช์กว้างตามข้อความ ไม่ให้ป้ายตกบรรทัด (เจ้าของงานทัก 18 ต.ค. 2569) */}
          <Grid item xs={12} md='auto' display='flex' alignItems='center'>
            <FormControlLabel
              sx={{ m: 0, whiteSpace: 'nowrap' }}
              control={
                <Switch
                  checked={stampSku || jobType === 'SKU_STAMP'}
                  disabled={jobType === 'SKU_STAMP'}
                  onChange={(e) => setStampSku(e.target.checked)}
                />
              }
              label={
                <MDTypography variant='button' color='text'>
                  ใส่รหัสสินค้าด้วย
                </MDTypography>
              }
            />
          </Grid>

          <Grid item xs={12} md display='flex' alignItems='center'>
            <MDButton variant='gradient' color='dark' fullWidth disabled={!canRun} onClick={submit}>
              <Icon>play_arrow</Icon>&nbsp;สั่งงาน{startCost}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>

      {/* ── รายการงาน ───────────────────────────────────────────────────── */}
      <MDBox mt={3} mb={1.5} display='flex' alignItems='center' gap={1} flexWrap='wrap'>
        {eyebrow('งานของสินค้านี้')}
        <MDTypography variant='caption' color='text' sx={{ opacity: 0.6 }}>
          {list.length > 0 ? `${list.length} รายการ` : ''}
        </MDTypography>
        {canViewAll && (
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Switch
                size='small'
                checked={scopeAll}
                onChange={(e) => {
                  scopeRef.current = e.target.checked;
                  setScopeAll(e.target.checked);
                  dl.clear();
                  loadList();
                }}
              />
            }
            label={<MDTypography variant='caption'>ดูของทุกคน</MDTypography>}
          />
        )}
        <MDBox flexGrow={1} />
        <SelectBar
          on={dl.on}
          onToggle={dl.toggleMode}
          selected={dl.count}
          total={dlAll.length}
          onAll={() => dl.addAll(dlAll)}
          onNone={dl.clear}
          onDownload={() => dl.run(`${dlName}_จัดการภาพ`, showNotify)}
          busy={dl.busy}
          progress={dl.progress}
        />
      </MDBox>

      {list.length === 0 ? (
        <MDBox
          py={6}
          display='flex'
          flexDirection='column'
          alignItems='center'
          gap={1}
          sx={{ border: '1px dashed', borderColor: 'grey.400', borderRadius: 2 }}
        >
          <Icon sx={{ fontSize: 40, opacity: 0.3 }}>auto_fix_high</Icon>
          <MDTypography variant='body2' color='text' sx={{ opacity: 0.7 }}>
            {scopeAll
              ? 'ยังไม่มีใครสั่งงานภาพให้สินค้านี้'
              : meta.can_run === 'Y' || meta.can_run === null
              ? 'คุณยังไม่เคยสั่งงานภาพให้สินค้านี้'
              : canViewAll
              ? 'คุณยังไม่เคยสั่งงานภาพให้สินค้านี้ — เปิด "ดูของทุกคน" เพื่อดูงานของคนอื่น'
              : 'บัญชีนี้เห็นเฉพาะงานที่ตัวเองสั่ง — ขอสิทธิ์ "ดู/ตรวจรับงาน AI ของทุกคน" จากผู้ดูแลระบบเพื่อดูงานของคนอื่น'}
          </MDTypography>
        </MDBox>
      ) : (
        <Grid container spacing={2.5}>
          {list.map((row) => {
            const st = statusList[row.status] || { name: row.status, color: 'default' };
            const working = row.status === 'QUEUED' || row.status === 'PROCESSING';
            const saved = row.approved === 'Y';
            /* ป้ายบนรูปขึ้นเฉพาะสถานะที่ต้องรู้ · กดที่รูป = ตรวจภาพ (18 ต.ค. 2569) */
            let badge = null;
            if (saved) badge = <Chip size='small' color='success' label='อยู่ในแกลเลอรีแล้ว' />;
            else if (!working && row.status !== 'DONE') {
              badge = (
                <Chip
                  size='small'
                  color={st.color === 'default' ? undefined : st.color}
                  label={st.name}
                />
              );
            }

            return (
              <Grid item xs={12} sm={6} md={4} key={row.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    boxShadow: 'none',
                    overflow: 'hidden',
                  }}
                >
                  {/* ก่อน / หลัง คู่กัน เห็นผลต่างทันที */}
                  <ReviewThumb
                    onReview={row.output_url ? () => setReviewId(row.id) : null}
                    label={`ตรวจภาพงาน ${row.id}`}
                    select={
                      row.output_url ? dl.selectProps(dlItem(row), `เลือกภาพงาน ${row.id}`) : null
                    }
                    sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'grey.200' }}
                  >
                    <MDBox
                      sx={{
                        width: '50%',
                        aspectRatio: '1 / 1',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      {row.input_url && (
                        <img
                          src={row.input_url}
                          alt={`in-${row.id}`}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      )}
                    </MDBox>
                    <MDBox
                      sx={{
                        width: '50%',
                        aspectRatio: '1 / 1',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {row.output_url ? (
                        <img
                          src={row.output_url}
                          alt={`out-${row.id}`}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : working ? (
                        <MDBox display='flex' flexDirection='column' alignItems='center' gap={1}>
                          <CircularProgress size={24} color='inherit' sx={{ opacity: 0.4 }} />
                          <MDTypography variant='caption' color='text' sx={{ opacity: 0.6 }}>
                            {st.name}
                          </MDTypography>
                        </MDBox>
                      ) : (
                        <Icon sx={{ fontSize: 30, opacity: 0.25 }}>block</Icon>
                      )}
                    </MDBox>
                  </ReviewThumb>

                  <MDBox p={1.5} display='flex' flexDirection='column' gap={0.5} flexGrow={1}>
                    {badge && <MDBox>{badge}</MDBox>}
                    <MDTypography variant='caption' color='text' sx={{ opacity: 0.75 }}>
                      {typeName[row.job_type] || row.job_type}
                      {row.stamp_sku === 'Y' ? ' + ใส่รหัส' : ''}
                      {` · ${jobCreditText(row, meta.job_types)}`}
                      {scopeAll ? ` · ${row.created_by_name || 'ระบบ'}` : ''}
                    </MDTypography>

                    <MDTypography
                      variant='caption'
                      color='text'
                      sx={{ opacity: 0.6 }}
                      title={`สั่งเมื่อ ${jobDateText(row.created_at, true)}${
                        row.finished_at ? ` · เสร็จเมื่อ ${jobDateText(row.finished_at, true)}` : ''
                      }`}
                    >
                      {row.finished_at
                        ? `เสร็จ ${jobDateText(row.finished_at)}`
                        : `สั่ง ${jobDateText(row.created_at)}`}
                    </MDTypography>
                    {/* เหตุผลภาษาไทย · รหัสดิบอยู่ใน title ให้ชี้เมาส์ดู (16 ต.ค. 2569) */}
                    {row.status === 'FAILED' && row.error_msg && (
                      <MDTypography
                        variant='caption'
                        color='error'
                        sx={{ opacity: 0.9 }}
                        title={row.error_msg}
                      >
                        {jobErrorText(row.error_msg) || 'เกิดข้อผิดพลาด'}
                      </MDTypography>
                    )}

                    {/* ปุ่มที่เขียนข้อมูลซ่อนเมื่อบัญชีไม่มีสิทธิ์สั่งงาน — backend ปฏิเสธอยู่แล้ว (16 ต.ค. 2569)
                        งานรอคิวยกเลิกได้จากเมนู ⋯ · งานที่กำลังทำไม่มีปุ่ม (18 ต.ค. 2569) */}
                    {(!working || (row.status === 'QUEUED' && meta.can_run === 'Y')) && (
                      <MDBox mt='auto' pt={1}>
                        <ActionRow>
                          {row.status === 'DONE' && !saved && meta.can_run === 'Y' ? (
                            <MDButton
                              size='small'
                              variant='gradient'
                              color='info'
                              onClick={() => approve(row)}
                              data-primary=''
                              sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                            >
                              <Icon>collections</Icon>&nbsp;เก็บเข้าแกลเลอรี
                            </MDButton>
                          ) : row.status === 'FAILED' && meta.can_run === 'Y' ? (
                            <MDButton
                              size='small'
                              variant='outlined'
                              color='dark'
                              disabled={!canRun}
                              onClick={() => setRetryRow({ ...row, sku, product_id: id })}
                              data-primary=''
                              sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                            >
                              <Icon>refresh</Icon>&nbsp;ทำใหม่
                            </MDButton>
                          ) : saved && onOpenGallery ? (
                            <GalleryLink
                              productId={id}
                              token={app.token}
                              onOpen={onOpenGallery}
                              fullWidth
                            />
                          ) : (
                            <MDBox flexGrow={1} />
                          )}
                          {row.output_url && (
                            <DownloadButton
                              label={`ดาวน์โหลดภาพงาน ${row.id}`}
                              onClick={() => dl.one(row.output_url, dlItem(row).name, showNotify)}
                            />
                          )}
                          <CardMenu
                            label={`ตัวเลือกเพิ่มเติมของงาน ${row.id}`}
                            items={[
                              meta.can_run === 'Y' && {
                                label: row.status === 'QUEUED' ? 'ยกเลิก' : 'ลบ',
                                icon: row.status === 'QUEUED' ? 'cancel' : 'delete_outline',
                                danger: true,
                                onClick: () => setDelRow(row),
                              },
                            ]}
                          />
                        </ActionRow>
                      </MDBox>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            );
          })}
          {total > list.length ? (
            <Grid item xs={12}>
              <MDBox textAlign='center'>
                <MDButton
                  variant='text'
                  color='info'
                  onClick={() => {
                    limitRef.current += 50;
                    loadList();
                  }}
                >
                  แสดงเพิ่ม · {list.length} จาก {total}
                </MDButton>
              </MDBox>
            </Grid>
          ) : null}
        </Grid>
      )}

      {/* ── dialog ทำใหม่ · ยืนยันลบ/ยกเลิก ─────────────────────────────── */}
      <ImagePickerDialog
        open={pickOpen}
        images={images}
        main={mainImage}
        value={String(
          sourceId === 'main' || String(sourceId) === String(mainImage.id) ? 'main' : sourceId
        )}
        onSelect={setSourceId}
        onClose={() => setPickOpen(false)}
      />
      <RetryImageJobDialog
        row={retryRow}
        types={meta.job_types}
        usage={usage}
        busy={loading}
        allowed={canRun}
        onClose={() => setRetryRow(null)}
        onConfirm={retry}
      />
      <Dialog open={Boolean(delRow)} onClose={() => setDelRow(null)} maxWidth='xs' fullWidth>
        <DialogTitle>{cancelling ? 'ยกเลิกงานนี้?' : 'ลบงานนี้ออกจากรายการ?'}</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2' color='text'>
            {cancelling ? 'งานจะไม่ถูกทำ และหายจากรายการ' : 'งานจะหายจากรายการนี้'}
            {delRow && delRow.approved === 'Y' ? ' (ภาพในแกลเลอรียังอยู่)' : ''}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' onClick={() => setDelRow(null)}>
            {cancelling ? 'กลับ' : t(`element.cancel`)}
          </MDButton>
          <MDButton variant='gradient' color='error' onClick={remove}>
            <Icon>{cancelling ? 'cancel' : 'delete'}</Icon>&nbsp;
            {cancelling ? 'ยกเลิกงาน' : t(`element.delete`)}
          </MDButton>
        </DialogActions>
      </Dialog>

      <ImageReview
        items={list
          .filter((row) => row.output_url)
          .map((row) => ({
            id: row.id,
            title: `${sku || `สินค้า #${id}`} · งาน #${row.id}`,
            src: row.output_url,
            fileName: dlItem(row).name,
            beforeSrc: row.input_url,
            approved: row.approved === 'Y',
            canApprove: row.status === 'DONE' && meta.can_run === 'Y',
            row,
          }))}
        selectedId={reviewId}
        onChange={setReviewId}
        onClose={() => setReviewId(null)}
        onApprove={approve}
        busy={loading}
      />
      {notify}

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </MDBox>
  );
}

ProductImageJob.defaultProps = { sku: '', onOpenGallery: null };
ProductImageJob.propTypes = { sku: PropTypes.string, onOpenGallery: PropTypes.func };
export default ProductImageJob;
